import { PublicApiError } from "./http";

export const PARTICIPATION_API_IDS = ["03", "04"] as const;
export const PARTICIPATION_STALE_AFTER_MS = 120_000;
export const MAX_UPSTREAM_RESPONSE_BYTES = 64 * 1024;
export const UPSTREAM_TIMEOUT_MS = 5_000;

export type ParticipationApiId = (typeof PARTICIPATION_API_IDS)[number];

export interface ParticipationAggregate {
  apiId: ParticipationApiId;
  totalCount: number;
  authenticatedCount: number;
  unauthenticatedCount: number;
  participationRate: number;
  dataVersion: number;
  refreshedAt: string;
  sourceGeneratedAt: string | null;
  fetchedAt: string;
  stale: boolean;
}

interface UpstreamMeta {
  apiId: ParticipationApiId;
  dataVersion: number;
  refreshedAt: string;
  sourceGeneratedAt: string | null;
  count: number;
}

interface ValidatedUpstreamResponse {
  meta: UpstreamMeta;
  data: {
    totalCount: number;
    authenticatedCount: number;
    unauthenticatedCount: number;
  };
}

const UPSTREAM_META_KEYS = new Set([
  "schemaVersion",
  "requestId",
  "apiId",
  "dataVersion",
  "refreshedAt",
  "sourceGeneratedAt",
  "refreshPerformed",
  "count",
]);
const UPSTREAM_RESPONSE_KEYS = new Set(["ok", "meta", "data"]);
const UPSTREAM_DATA_KEYS = new Set([
  "totalCount",
  "authenticatedCount",
  "unauthenticatedCount",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isValidIsoDate(value: unknown): value is string {
  return typeof value === "string" && value.length <= 64 && Number.isFinite(Date.parse(value));
}

function isNonNegativeSafeInteger(value: unknown): value is number {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0;
}

function isPositiveSafeInteger(value: unknown): value is number {
  return isNonNegativeSafeInteger(value) && value > 0;
}

export function isParticipationApiId(value: string): value is ParticipationApiId {
  return value === "03" || value === "04";
}

export function readApiId(url: URL): ParticipationApiId {
  const keys = Array.from(url.searchParams.keys());
  const values = url.searchParams.getAll("apiId");

  if (keys.length !== 1 || keys[0] !== "apiId" || values.length !== 1) {
    throw new PublicApiError(
      400,
      "INVALID_QUERY",
      "apiIdには03または04を1つ指定してください。",
    );
  }

  const apiId = values[0];
  if (!apiId || !isParticipationApiId(apiId)) {
    throw new PublicApiError(
      400,
      "INVALID_API_ID",
      "apiIdには03または04を指定してください。",
    );
  }

  return apiId;
}

function validateUpstreamResponse(
  value: unknown,
  expectedApiId: ParticipationApiId,
): ValidatedUpstreamResponse {
  if (!isRecord(value) || value.ok !== true || !isRecord(value.meta) || !isRecord(value.data)) {
    throw new PublicApiError(502, "UPSTREAM_INVALID_RESPONSE", "参加状況を取得できませんでした。");
  }

  const { meta, data } = value;
  const totalCount = data.totalCount;
  const authenticatedCount = data.authenticatedCount;
  const unauthenticatedCount = data.unauthenticatedCount;

  if (
    Object.keys(value).some((key) => !UPSTREAM_RESPONSE_KEYS.has(key))
    || Object.keys(meta).some((key) => !UPSTREAM_META_KEYS.has(key))
    || Object.keys(data).some((key) => !UPSTREAM_DATA_KEYS.has(key))
    || meta.schemaVersion !== 1
    || typeof meta.requestId !== "string"
    || meta.requestId.length < 1
    || meta.requestId.length > 128
    || meta.apiId !== expectedApiId
    || !isPositiveSafeInteger(meta.dataVersion)
    || !isValidIsoDate(meta.refreshedAt)
    || !(meta.sourceGeneratedAt === null || isValidIsoDate(meta.sourceGeneratedAt))
    || !isNonNegativeSafeInteger(meta.count)
    || meta.refreshPerformed !== false
    || !isNonNegativeSafeInteger(totalCount)
    || meta.count !== totalCount
    || !isNonNegativeSafeInteger(authenticatedCount)
    || authenticatedCount > totalCount
    || !isNonNegativeSafeInteger(unauthenticatedCount)
    || unauthenticatedCount !== totalCount - authenticatedCount
  ) {
    throw new PublicApiError(502, "UPSTREAM_INVALID_RESPONSE", "参加状況を取得できませんでした。");
  }

  return {
    meta: {
      apiId: expectedApiId,
      dataVersion: meta.dataVersion,
      refreshedAt: meta.refreshedAt,
      sourceGeneratedAt: meta.sourceGeneratedAt,
      count: meta.count,
    },
    data: {
      totalCount,
      authenticatedCount,
      unauthenticatedCount,
    },
  };
}

export function aggregateParticipation(
  value: unknown,
  expectedApiId: ParticipationApiId,
  fetchedAtMs: number,
): ParticipationAggregate {
  const upstream = validateUpstreamResponse(value, expectedApiId);
  const { totalCount, authenticatedCount, unauthenticatedCount } = upstream.data;
  const participationRate = totalCount === 0
    ? 0
    : Math.round((authenticatedCount / totalCount) * 1_000) / 10;
  const refreshedAtMs = Date.parse(upstream.meta.refreshedAt);
  const freshnessAgeMs = fetchedAtMs - refreshedAtMs;
  const sourceAgeMs = upstream.meta.sourceGeneratedAt === null
    ? freshnessAgeMs : fetchedAtMs - Date.parse(upstream.meta.sourceGeneratedAt);

  return {
    apiId: expectedApiId,
    totalCount,
    authenticatedCount,
    unauthenticatedCount,
    participationRate,
    dataVersion: upstream.meta.dataVersion,
    refreshedAt: upstream.meta.refreshedAt,
    sourceGeneratedAt: upstream.meta.sourceGeneratedAt,
    fetchedAt: new Date(fetchedAtMs).toISOString(),
    stale: freshnessAgeMs > PARTICIPATION_STALE_AFTER_MS || freshnessAgeMs < -30_000
      || sourceAgeMs > PARTICIPATION_STALE_AFTER_MS || sourceAgeMs < -30_000,
  };
}

export async function readBoundedJson(
  response: Response,
  maxBytes = MAX_UPSTREAM_RESPONSE_BYTES,
): Promise<unknown> {
  const contentType = response.headers.get("Content-Type")?.toLowerCase() ?? "";
  if (!contentType.startsWith("application/json")) {
    await response.body?.cancel();
    throw new PublicApiError(502, "UPSTREAM_INVALID_RESPONSE", "参加状況を取得できませんでした。");
  }

  const contentLengthHeader = response.headers.get("Content-Length");
  if (contentLengthHeader !== null) {
    const contentLength = Number(contentLengthHeader);
    if (!Number.isSafeInteger(contentLength) || contentLength < 0 || contentLength > maxBytes) {
      await response.body?.cancel();
      throw new PublicApiError(502, "UPSTREAM_RESPONSE_TOO_LARGE", "参加状況を取得できませんでした。");
    }
  }

  if (!response.body) {
    throw new PublicApiError(502, "UPSTREAM_INVALID_RESPONSE", "参加状況を取得できませんでした。");
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let receivedBytes = 0;
  let text = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      receivedBytes += value.byteLength;
      if (receivedBytes > maxBytes) {
        await reader.cancel();
        throw new PublicApiError(
          502,
          "UPSTREAM_RESPONSE_TOO_LARGE",
          "参加状況を取得できませんでした。",
        );
      }
      text += decoder.decode(value, { stream: true });
    }
    text += decoder.decode();
  } finally {
    reader.releaseLock();
  }

  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new PublicApiError(502, "UPSTREAM_INVALID_RESPONSE", "参加状況を取得できませんでした。");
  }
}
